import Annotation from "./annotation";
import { Directions } from "../../config/config";

export default class SliceSelector extends Annotation {
  constructor() {
    super();

    this.color = "rgba(0,176,240, 0.5)";
    this.colorActive = "#00b0f0";
    this.compositeColor = "rgba(255,0,255,0.5)";
    this.compositeColorActive = "magenta";
    this.lineWidth = 1;
    this.sliceCurrentWidth = 5;
    this.boxWidth = 3;
    this.triangleLength = 15;
    this.triangleInternalAngle = Math.PI / 8;

    this.slices = [];
    this.sliceCurrent = null;
    this.sliceBounds = null;
    this.slicesVertical = false;

    this.composites = [];
    this.compositeCurrent = null;
    this.compositeBounds = null;
    this.compositesVertical = true;

    this.minX = null;
    this.maxX = null;
    this.minY = null;
    this.maxY = null;

    this.drawAllSlices = true;
    this.showExtension = true;
    this.showBindingBox = false;

    this.canvasSize = null;
    this.drawBoundingBoxWidth = null;
    this.drawBoundingBoxHeight = null;

    this.isRadial = false;
  }

  draw(ctx) {
    if (!this.canvasSize) 
      return;

    let w = this.canvasSize.w;
    let h = this.canvasSize.h;

    if (this.slices.length)
      this.drawSliceCollection(ctx, this.slices, this.sliceCurrent, w, h, this.color, this.colorActive);
    if (this.composites.length)
      this.drawSliceCollection(ctx, this.composites, this.compositeCurrent, w, h, this.compositeColor, this.compositeColorActive);

    if (this.showBindingBox) this.drawBoundingBox(ctx, w, h);
  }

  drawSliceCollection(ctx, slices, current, width, height, color, colorActive) {
    ctx.lineWidth = this.lineWidth;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    if (this.drawAllSlices) slices.forEach(slice => this.drawSlice(ctx, slice, width, height));
    ctx.strokeStyle = colorActive;
    ctx.fillStyle = colorActive;
    ctx.lineWidth = this.showExtension ? this.sliceCurrentWidth : this.boxWidth;
    let slice = slices.find(s => s.index === current);
    if (slice) this.drawSlice(ctx, slice, width, height);
  }

  drawSlice(ctx, slice, width, height) {
    ctx.beginPath();
    ctx.moveTo(slice.eX * width, slice.eY * height);
    ctx.lineTo(slice.sX * width, slice.sY * height);
    ctx.stroke();
    ctx.closePath();
    if (!this.showExtension) this.drawTriangleCaps(ctx, slice, width, height);
  }

  drawTriangleCaps(ctx, slice, width, height) {
    let angle = Math.atan2(slice.eY - slice.sY, slice.eX - slice.sX);
    this.drawTriangle(ctx, slice.sX * width, slice.sY * height, angle + Math.PI);
    this.drawTriangle(ctx, slice.eX * width, slice.eY * height, angle);
  }

  drawTriangle(ctx, x, y, angle) {
    let { x: x1, y: y1 } = this.getTriangleCoordinates(x, y, angle + this.triangleInternalAngle);
    let { x: x2, y: y2 } = this.getTriangleCoordinates(x, y, angle - this.triangleInternalAngle);
    ctx.beginPath();
    ctx.moveTo(x, y);
    ctx.lineTo(x1, y1);
    ctx.lineTo(x2, y2);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.fill();
    ctx.closePath();
  }

  getTriangleCoordinates(x, y, angle) {
    return {
      x: x + this.triangleLength * Math.cos(angle),
      y: y + this.triangleLength * Math.sin(angle)
    };
  }

  drawBoundingBox(ctx, w, h) {
    this.drawBoundingBoxWidth = Math.abs(this.maxX - this.minX) * w;
    this.drawBoundingBoxHeight = Math.abs(this.minY - this.maxY) * h;
    ctx.lineWidth = this.boxWidth;
    ctx.strokeStyle = this.compositeColorActive;
    ctx.fillStyle = this.compositeColorActive;
    ctx.strokeRect(this.minX * w, this.minY * h, this.drawBoundingBoxWidth, this.drawBoundingBoxHeight);
  }

  updateSlices(slices) {
    let { processed, vertical } = this.processSliceCollection(slices);
    this.slices = processed;
    this.slicesVertical = vertical;
    this.signalReady();
  }

  updateCompositeSlices(slices) {
    let { processed, vertical } = this.processSliceCollection(slices);
    this.composites = processed;
    this.compositesVertical = vertical;
    this.signalReady();
  }

  processSliceCollection(slices) {
    if (!slices || !slices.length) return { processed: [] };
    let vertical = slices[0].vertical;
    let exStart = 0.02;
    let exEnd = 0.98;
    let exLength = slices.length > 1 ? (exEnd - exStart) / (slices.length - 1) : 0;
    let processed = slices.map(slice => {
      return {
        index: slice.index,
        imgKey: slice.imgKey,
        sX: slice.startPoint.x,
        sY: slice.startPoint.y,
        eX: slice.endPoint.x,
        eY: slice.endPoint.y
      }
    }).sort((a, b) => {
      if (vertical) return a.eX - b.eX;
      return a.eY - b.eY;
    });

    processed.forEach((slice, index) => {
      if (vertical) {
        slice.exStartY = 0.02;
        slice.exEndY = 0.10;
        slice.exStartX = slice.exEndX = exStart + index * exLength;
      } else {
        slice.exStartX = 0.02;
        slice.exEndX = 0.10;
        slice.exStartY = slice.exEndY = exStart + index * exLength;
      }
    });

    return { processed, vertical };
  }

  updateBounds() {
    this.minX = this.maxX = this.minY = this.maxY = 0.5;
    if (this.sliceBounds) this.applyBounds(this.sliceBounds);
    if (this.compositeBounds) this.applyBounds(this.compositeBounds);
  }

  applyBounds(bounds) {
    this.maxX = Math.max(this.maxX, bounds.maxX);
    this.minX = Math.min(this.minX, bounds.minX);
    this.maxY = Math.max(this.maxY, bounds.maxY);
    this.minY = Math.min(this.minY, bounds.minY);
  }

  updateSliceBounds(bounds) {
    this.sliceBounds = bounds;
    this.updateBounds();
    this.signalReady();
  }

  updateCompositeSliceBounds(bounds) {
    this.compositeBounds = bounds;
    this.updateBounds();
    this.signalReady();
  }

  updatesliceCurrent(slice) {
    this.sliceCurrent = slice ? slice.index : null;
    this.signalReady();
  }

  updatecompositeCurrent(slice) {
    this.compositeCurrent = slice ? slice.index : null;
    this.signalReady();
  }

  getHoveredSlices(x, y, isDragging, outOfBoundsDirections) {
    return {
      slice: isDragging && outOfBoundsDirections == Directions.Vertical ? null : this.sliceAt(this.slices, this.slicesVertical, x, y),
      composite: isDragging && outOfBoundsDirections == Directions.Horizontal ? null :this.sliceAt(this.composites, this.compositesVertical, x, y)
    };
  }

  sliceAt(slices, vertical, x, y) {
    if (!slices.length) return null;

    let sorted = [...slices];
    if (this.isRadial) {
      sorted.sort((a, b) => this.getDistanceFromSlice(a, x, y) - this.getDistanceFromSlice(b, x, y));
      return sorted[0].index;
    }
    else if (vertical) sorted.sort((a, b) => a.sX - b.sX);
    else sorted.sort((a, b) => a.sY - b.sY);

    if (this.showExtension && x < sorted[0].sX) return this.findSliceInExtension(sorted, x, y);
    if (vertical)return this.findVerticalSlice(sorted, x, y);
    return this.findHorizontalSlice(sorted, x, y);
  }

  findSliceInExtension(slices, x, y) {
    // for now - not supporting vertical extensions
    let less = (slice, x, y) => y < slice.exStartY;
    let greater = (slice, x, y) => y >= slice.exStartY;
    return this.binarySearch(slices, x, y, less, greater);
  }

  findHorizontalSlice(slices, x, y) {
    let less = (slice, x, y) => y < slice.eY;
    let greater = (slice, x, y) => y >= slice.eY;
    return this.binarySearch(slices, x, y, less, greater);
  }

  findVerticalSlice(slices, x, y) {
    let less = (slice, x) => x < slice.eX;
    let greater = (slice, x) => x >= slice.eX;
    return this.binarySearch(slices, x, y, less, greater);
  }
  
  getDistanceFromSlice(slice, x, y) {
    let dy = slice.eY - slice.sY;
    let dx = slice.eX - slice.sX;

    let numerator = Math.abs(dy * x - dx * y
      + slice.eX * slice.sY - slice.eY * slice.sX);
    let denominator = Math.sqrt(dy * dy + dx * dx);
    return numerator / denominator;
  }

  binarySearch(slices, x, y, less, greater) {
    if (less(slices[0], x, y)) return slices[0].index;
    if (greater(slices[slices.length - 1], x, y)) return slices[slices.length - 1].index;

    let l = 0, r = slices.length, m = 0;
    while (l < (r - 1)) {
      m = Math.floor((l + r) / 2);
      if (less(slices[m], x, y)) r = m;
      else l = m;
    }
    return slices[l].index;
  }

  updateDrawAllSlices(drawAllSlices) {
    this.drawAllSlices = drawAllSlices;
    this.signalReady();
  }
  updateShowExtension(showExtension) {
    this.showExtension = showExtension;
    this.signalReady();
  }
  updateShowBindingBox(showBindingBox) {
    this.showBindingBox = showBindingBox;
    this.signalReady();
  }
}